import React, { useCallback, useMemo, useRef, useState } from 'react';
import { App as AntdApp } from 'antd';

import { rankItem, isStaleOrder } from '../services/items';
import { needsAttestation } from '../services/mfa';
import { normalizeId } from '../utils/ids';
import { isLinkedRow } from '../utils/linkedRecords';
import { isPendingIntake } from '../services/records';
import { estimatedRank, placementAt, placementBeside, stepPlacement } from '../utils/rank';
import { cellEditability } from '../components/items/cardTableModel';
import { ARROW_DOWN, ARROW_UP } from '../components/icons';

/**
 * A MANUAL ORDER moved from a list (KPR-1396): the table's rows or the
 * board's columns, sorted by a rank element (utils/rank.js).
 *
 *   const order = useRankMove({ rank: { element, direction }, rows, collection,
 *     collectionId, cardsById, getElementsForCard, onItemUpdated, onReload });
 *   order.blocked(item)              why this item cannot move, or null
 *   order.moveTo(item, toIndex)      a drop: its new place among `rows`
 *   order.moveTo(item, i, laneRows)  …among other rows (a board column)
 *   order.moveBeside(item, anchor, 'before' | 'after', laneRows?)  a drop on a row
 *   order.step(item, 'up' | 'down' | 'top', laneRows?)   the item's menu
 *   order.attestation                the card's signature prompt, or null
 *
 * - **The neighbours, not a number**: the rows on screen say which items it
 *   lands between; keepr writes the value (`POST /api/items/{id}/rank`). Rows
 *   of another collection (a sub-collection's, a linked record) or of a card
 *   without the element are skipped: the nearest row that shares the order
 *   is sent.
 * - **Optimistic**: the item is drawn in its new place (an estimated rank)
 *   while the request runs; a refusal puts it back and says why in one line.
 * - **Someone else moved things** (409 `stale_item` or `rank_stale`): the list
 *   is read again and says so. A renumbered order is read again too.
 * - **A card that asks for a signature** (428): the prompt, then the move.
 */
export default function useRankMove({
	rank = null,
	rows = [],
	collection = null,
	collectionId = null,
	cardsById = null,
	getElementsForCard,
	onItemUpdated,
	onReload,
}) {
	const { message } = AntdApp.useApp();
	const [attestation, setAttestation] = useState(null);
	const busyRef = useRef(new Set());
	const element = rank?.element || null;
	const direction = rank?.direction || 'asc';

	const defOf = useCallback((item) => {
		const defs = typeof getElementsForCard === 'function' ? getElementsForCard(item?.card_id) || [] : [];
		return defs.find(def => def?.name === element) || null;
	}, [getElementsForCard, element]);

	const homeOf = useCallback(item => normalizeId(item?.collection_id) || normalizeId(collectionId), [collectionId]);

	/** Why this item cannot be moved here, or null. */
	const blocked = useCallback((item) => {
		if (!element) return 'This list is not in a manual order.';
		const def = defOf(item);
		if (!def) return 'This item’s card is not in this order.';
		const verdict = cellEditability({ column: { element }, def, item, collection, cardsById });
		if (verdict.editable || verdict.rank) return null;
		return verdict.reason || 'You cannot move this item.';
	}, [element, defOf, collection, cardsById]);

	const neighbourFor = useCallback((item) => {
		const home = homeOf(item);
		// An unreviewed submission is outside the order (keepr-api rankUtils).
		return row => !isLinkedRow(row) && !isPendingIntake(row) && homeOf(row) === home && Boolean(defOf(row));
	}, [homeOf, defOf]);

	const send = useCallback(async (item, placement, list) => {
		const id = normalizeId(item?._id);
		if (!element || !placement || !id) return { ok: false, reason: 'nothing' };
		if (busyRef.current.has(id)) return { ok: false, reason: 'busy' };
		const reason = blocked(item);
		if (reason) {
			message.error(`Not moved. ${reason}`);
			return { ok: false, reason: 'refused', message: reason };
		}
		busyRef.current.add(id);
		const before = item;
		const guess = estimatedRank(list, placement, element);
		if (guess !== null) onItemUpdated?.({ ...item, elements: { ...(item.elements || {}), [element]: guess } });

		const request = config => rankItem(id, { element, ...placement, ifUpdatedAt: item?.updatedAt || null }, config);
		const signed = () => request().catch((error) => {
			if (!needsAttestation(error)) throw error;
			return new Promise((resolve, reject) => {
				setAttestation({
					mode: error?.response?.data?.mode || 'pin',
					cardId: normalizeId(item?.card_id),
					onSigned: token => request({ headers: { 'X-Attestation': token } }).then(resolve, reject),
					onCancel: () => reject(error),
				});
			});
		});
		try {
			const answer = await signed();
			if (answer?.item?._id) onItemUpdated?.(answer.item);
			if (answer?.renumbered) onReload?.();
			return { ok: true, item: answer?.item || null };
		} catch (error) {
			onItemUpdated?.(before);
			const status = error?.response?.status;
			const data = error?.response?.data;
			if (isStaleOrder(error)) {
				if (data?.item?._id) onItemUpdated?.(data.item);
				message.warning('The list changed since it loaded, so it was not moved. It has been read again.');
				onReload?.();
				return { ok: false, reason: 'stale' };
			}
			if (needsAttestation(error)) {
				message.info('Not moved — this card asks you to sign each save.');
				return { ok: false, reason: 'attestation' };
			}
			if (status === 404) {
				message.warning('Not moved. An item here was deleted or is no longer yours to see; the list has been read again.');
				onReload?.();
				return { ok: false, reason: 'gone' };
			}
			const said = (typeof data?.message === 'string' && data.message) || null;
			let text;
			if (status === 403) text = said || 'You cannot change this item.';
			else if (status === 409 && data?.code === 'item_locked') text = said || 'This item is locked, so it cannot be moved.';
			else text = said || 'Something went wrong, and the item was not moved.';
			message.error(`Not moved. ${text}`);
			return { ok: false, reason: 'refused', message: text };
		} finally {
			busyRef.current.delete(id);
		}
	}, [element, blocked, message, onItemUpdated, onReload]);

	/** A drop: `item` lands at `toIndex` among `list` (the rows it was dragged in). */
	const moveTo = useCallback((item, toIndex, list = rows) => {
		const placement = placementAt(list, item?._id, toIndex, { direction, canNeighbour: neighbourFor(item), element });
		if (!placement) return Promise.resolve({ ok: false, reason: 'nothing' });
		return send(item, placement, list);
	}, [rows, direction, neighbourFor, send, element]);

	/** A drop on another row: just before it, or just after it (`side`). */
	const moveBeside = useCallback((item, anchor, side, list = rows) => {
		const placement = placementBeside(list, item?._id, anchor?._id || anchor, side, { direction, canNeighbour: neighbourFor(item), element });
		if (!placement) return Promise.resolve({ ok: false, reason: 'nothing' });
		return send(item, placement, list);
	}, [rows, direction, neighbourFor, send, element]);

	/** Move up / Move down / Move to top. */
	const step = useCallback((item, kind, list = rows) => {
		const placement = stepPlacement(list, item?._id, kind, { direction, canNeighbour: neighbourFor(item), element });
		if (!placement) return Promise.resolve({ ok: false, reason: 'nothing' });
		return send(item, placement, list);
	}, [rows, direction, neighbourFor, send, element]);

	/**
	 * The item menu's entries (the phone's way, and the keyboard's): Move up,
	 * Move down, Move to top, each disabled where it has nowhere to go.
	 * `list` is the rows the item sits among (a board column).
	 */
	const menuActions = useCallback((item, list = rows) => {
		if (!element) return [];
		const reason = blocked(item);
		const ids = (list || []).map(row => normalizeId(row?._id));
		const at = ids.indexOf(normalizeId(item?._id));
		const can = neighbourFor(item);
		const hasAbove = at > 0 && (list || []).slice(0, at).some(can);
		const hasBelow = at >= 0 && (list || []).slice(at + 1).some(can);
		const entry = (key, label, disabled, kind) => ({
			key: `rank-${key}`,
			group: 'record',
			icon: React.createElement(kind === 'down' ? ARROW_DOWN : ARROW_UP, { 'aria-hidden': true }),
			label,
			disabled: Boolean(reason) || disabled,
			title: reason || undefined,
			onClick: () => { step(item, kind, list); },
		});
		return [
			entry('up', 'Move up', !hasAbove, 'up'),
			entry('down', 'Move down', !hasBelow, 'down'),
			entry('top', 'Move to top', !hasAbove, 'top'),
		];
	}, [element, rows, blocked, neighbourFor, step]);

	return useMemo(() => ({
		active: Boolean(element),
		element,
		direction,
		blocked,
		moveTo,
		moveBeside,
		step,
		menuActions,
		attestation,
		setAttestation,
	}), [element, direction, blocked, moveTo, moveBeside, step, menuActions, attestation]);
}
